import React, { useEffect, useState } from 'react';
import classes from './LoginPage.module.scss';
import { Button, Flex, Form, Input, message, Space, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { getSignInScheme } from '../model/scheme';
import { useIntl } from 'react-intl';
import { AppDispatch } from 'shared/config/store/store';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { CustomCardContainer } from 'shared/ui';
import { useLoginMutation } from '../api';
import { useLazyGetUserInfoQuery, useSaveGtagMutation } from 'entities/user';
import { useAddNotificationTokenMutation } from 'entities/notification/api';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { setUserMeta } from 'shared/config/store/reducers/userSlice';
import { getErrorId, getSecondErrorId } from 'shared/libs/helpers';
import { ERROR_IDS } from 'shared/const/api';
import { ROUTES } from 'shared/const/routes';
import { ApiError } from 'shared/model/types';
import { GTAG_ID } from 'shared/const/app';

const SignIn = () => {
  const [loading, setLoading] = useState(false);
  const [showForbiddenModal, setShowForbiddenModal] = useState<boolean>(false);

  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const password = Form.useWatch('password', form);
  const formRules = getSignInScheme(t);
  const { showError, showSuccess } = useMessage();
  const [emailErr, setEmailErr] = useState<string[]>([]);
  const [passwordErr, setPasswordErr] = useState<string[]>([]);

  const [login, { isLoading }] = useLoginMutation();
  const [fetchUserInfo] = useLazyGetUserInfoQuery();
  const [saveGtag] = useSaveGtagMutation();
  const [addNotificationToken] = useAddNotificationTokenMutation();

  const onFinish = ({ email, password }: { email: string; password: string }) => {
    const formData = { email, password };
    dispatch(setActivity(true));
    login(formData)
      .unwrap()
      .then(({ response }) => {
        gtag('get', GTAG_ID, 'client_id', (clientID) => {
          saveGtag({ clientId: clientID as string, eventName: 'web_login_successful', gtag: GTAG_ID });
          gtag('event', 'web_login_successful', {
            user_id: response.id,
            email: response.email,
            verified: response.verified,
          });
        });

        if (!response.verified && response.email) {

          navigate({ pathname: ROUTES.CONFIRM_EMAIL.path, search: `email=${response.email}&phone=${response.phone}` },);
        } else if (!response.phoneVerified && response.phone) {
          navigate(ROUTES.CONFIRM_PHONE.path);
        } else {
          showSuccess(t({ id: 'loginPage.welcome' }));
          dispatch(setUserMeta(response));
          fetchUserInfo();
        }
      })
    .catch((error: ApiError) => {
      const errorId = getErrorId(error);
      const errorId2 = getSecondErrorId(error);

      if (errorId === 'prohibited.country.ip') {
        showError(t({ id: 'modals.prohibited.text' }));
        return;
      }

      if (errorId === ERROR_IDS.TWO_FA_NOT_PROVIDED) {
        navigate(ROUTES.TWO_FACTOR.path, { state: { email, password } });
        return;
      }

      if (
        errorId === 'screens.auth.login.errors.invalid' &&
        errorId2 !== 'errors.access.temporary.blocked'
      ) {
        showError(t({ id: 'loginPage.badCredentials' }));
        return;
      }

      if (errorId2 === 'errors.access.temporary.blocked') {
        showError(t({ id: 'errors.access.temporary.blocked' }));
        return;
      }

      showError(t({ id: 'loginPage.error' }));
    })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  const onFieldsChange = () => {
    setEmailErr(form.getFieldError('email'));
    setPasswordErr(form.getFieldError('password'));
  };

  return (
    <div className={classes.wrapper}>
      {/* <Index open={showForbiddenModal} onClose={() => setShowForbiddenModal(false)} /> */}
      <Flex flex={1} justify="center" align="center">
        <CustomCardContainer className={classes.card}>
          <Typography.Text className={classes.companyName}>{'Weststein'}</Typography.Text>
          <Form form={form} onFinish={onFinish} onFieldsChange={onFieldsChange}>
            <Form.Item
              help={<div id="error__email">{emailErr[0]}</div>}
              rules={formRules.email}
              name="email">
              <Input placeholder={t({ id: 'inputs.email' })} />
            </Form.Item>
            <Form.Item
              help={<div id="error__password">{passwordErr[0]}</div>}
              rules={formRules.password}
              name="password"
            >
              <Input.Password
                placeholder={t({ id: 'inputs.password' })}
                visibilityToggle={{
                  onVisibleChange: () => {
                    if (!password) navigate(ROUTES.RESET_PASSWORD.path);
                  }
                }}
                iconRender={(visible) => {
                  if (password) {
                    return visible ? <EyeOutlined /> : <EyeInvisibleOutlined />;
                  } else {
                    return <Typography.Link>{t({ id: 'mobile.login.input.forgot' })}</Typography.Link>;
                  }
                }}
              />
            </Form.Item>
            <Space direction="vertical" className="w100" size={8}>
              <Button loading={loading} color="primary" variant='solid' onClick={form.submit} block>
                {t({ id: 'buttons.login' })}
              </Button>
              <Button
                disabled={loading}
                onClick={() => navigate(ROUTES.SIGN_UP.path)}
                type="text"
                block>
                {t({ id: 'buttons.signup' })}
              </Button>
            </Space>
          </Form>
        </CustomCardContainer>
      </Flex>
    </div>
  );
};

export default SignIn;
